import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { useAuth } from '../context/AuthContext';
import { ArrowRight, Shield, Star, Wallet, MapPin } from 'lucide-react';
import SEO from '../components/SEO';

const About = () => {
  const navigate = useNavigate();
  const { isAuthenticated, isTrainer } = useAuth();

  return (
    <div className="min-h-screen bg-[#09090B] text-white" data-testid="about-page">
      <SEO
        title="About DojoDash | Mobile Training Network"
        description="DojoDash is a mobile training network connecting professional coaches with clients who want to train at home. Built by world-champion grappler Kade Tsitos."
      />
      {/* Hero */}
      <section className="relative px-4 sm:px-6 lg:px-8 pt-20 pb-16 border-b border-zinc-900">
        <div className="max-w-4xl mx-auto">
          <p className="text-xs uppercase tracking-[0.25em] text-[#C0A062] font-semibold mb-2" data-testid="about-eyebrow">
            About DojoDash
          </p>
          <p className="text-xs text-zinc-500 mb-6" data-testid="about-founded-by">
            Founded by Kade Anastasios Tsitos
          </p>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold leading-[1.05] tracking-tight max-w-3xl">
            We built the bridge between great coaches and the people who need them.
          </h1>
          <p className="mt-8 text-lg text-zinc-400 max-w-2xl leading-relaxed">
            DojoDash is a mobile training network, with professional coaches, real
            reviews, secure payment, and zero friction between a client wanting to train
            and a coach ready to teach.
          </p>
        </div>
      </section>

      {/* Origin */}
      <section className="px-4 sm:px-6 lg:px-8 py-20 border-b border-zinc-900">
        <div className="max-w-4xl mx-auto grid md:grid-cols-3 gap-10">
          <div className="md:col-span-1">
            <p className="text-xs uppercase tracking-[0.2em] text-zinc-500 font-semibold">01. Origin</p>
            <h2 className="text-2xl font-bold text-white mt-3">Why DojoDash exists</h2>
          </div>
          <div className="md:col-span-2 space-y-5 text-zinc-300 leading-relaxed">
            <p>
              The martial arts and fitness industry has a broken discovery problem. Great coaches, the ones who actually get results, are scrambling to find clients. And clients? They&apos;re stuck scrolling Yelp reviews and hoping for the best. DojoDash fixes both sides of that equation.
            </p>
          </div>
        </div>
      </section>

      {/* Founder */}
      <section className="px-4 sm:px-6 lg:px-8 py-20 border-b border-zinc-900" data-testid="founder-section">
        <div className="max-w-4xl mx-auto">
          <p className="text-xs uppercase tracking-[0.2em] text-zinc-500 font-semibold">02. Founder</p>
          <h2 className="text-2xl font-bold text-white mt-3 mb-10">Meet the founder.</h2>

          <div className="grid md:grid-cols-[200px_1fr] gap-8 items-start">
            {/* Founder photo */}
            <div
              className="w-40 h-40 sm:w-48 sm:h-48 rounded-full overflow-hidden border-2 border-[#C0A062]/40 mx-auto md:mx-0 bg-[#0d0d10]"
              data-testid="founder-photo"
            >
              <img
                src="https://customer-assets.emergentagent.com/job_doorguard-dev/artifacts/f1ly6pt2_image0.png"
                alt="Kade Anastasios Tsitos"
                className="w-full h-full object-cover"
                loading="lazy"
              />
            </div>

            <div>
              <h3 className="text-2xl font-bold text-white" data-testid="founder-name">
                Kade Anastasios Tsitos
              </h3>

              <div className="mt-6 space-y-5 text-zinc-300 leading-relaxed" data-testid="founder-story">
                <p>
                  I&apos;m a world champion with a degree in Exercise Science and over ten years of
                  coaching experience, and I still couldn&apos;t find clients.
                </p>
                <p>
                  For five years I did everything I could. Cold outreach, word of mouth, posting online.
                  I hit a drought. The problem was never my skills. It was the connections.
                </p>
                <p>
                  There are countless great coaches out there struggling to make a living. And on the
                  other side, people who want to train but don&apos;t have time to drive to a gym,
                  don&apos;t want to leave their house, or just want to learn in their own space on
                  their own schedule.
                </p>
                <p className="text-white font-semibold">
                  I built DojoDash because nobody, whether you teach BJJ, self-defense, or personal
                  training, should have to struggle to find great clients. And nobody looking to get
                  better should have to struggle to find a great coach. Not anymore.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* What we do */}
      <section className="px-4 sm:px-6 lg:px-8 py-20 border-b border-zinc-900">
        <div className="max-w-4xl mx-auto">
          <p className="text-xs uppercase tracking-[0.2em] text-zinc-500 font-semibold">02. What we do</p>
          <h2 className="text-2xl font-bold text-white mt-3 mb-10">A two-sided marketplace, on your terms.</h2>

          <div className="grid md:grid-cols-2 gap-6">
            <div className="bg-[#18181B] border border-zinc-800 rounded-lg p-8" data-testid="about-for-clients">
              <p className="text-xs uppercase tracking-[0.2em] text-[#C0A062] font-semibold mb-4">For clients</p>
              <p className="text-zinc-300 leading-relaxed">
                Browse professional coaches by specialty, Personal Training, Self-Defense, Brazilian Jiu-Jitsu.
                See real ratings. Book in seconds. Pay securely. Your coach comes to you.
              </p>
            </div>
            <div className="bg-[#18181B] border border-zinc-800 rounded-lg p-8" data-testid="about-for-trainers">
              <p className="text-xs uppercase tracking-[0.2em] text-[#C0A062] font-semibold mb-4">For trainers</p>
              <p className="text-zinc-300 leading-relaxed">
                Stop chasing clients. Set your rates, list your services, and let clients come to you.
                Trainers get paid fast, directly to their bank account.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Differentiators */}
      <section className="px-4 sm:px-6 lg:px-8 py-20 border-b border-zinc-900">
        <div className="max-w-4xl mx-auto">
          <p className="text-xs uppercase tracking-[0.2em] text-zinc-500 font-semibold">04. What makes us different</p>
          <h2 className="text-2xl font-bold text-white mt-3 mb-12">The four things we won&apos;t compromise on.</h2>

          <ul className="space-y-7">
            {[
              { icon: MapPin, title: 'Your preference, maximum convenience.', body: 'Train at home, in your garage, at the park, at work, etc. The coach adapts to you not the other way around.' },
              { icon: Star, title: 'Reviews you can actually trust.', body: 'Every rating comes from a verified account that booked and paid through DojoDash. No anonymous stars, no fake five-star padding. What you see is what that coach actually did.' },
              { icon: Wallet, title: 'Money moves clean.', body: 'No cash, no Venmo, no awkward payment conversations. Book and pay in the app. Coaches get paid directly to their bank account easy.' },
              { icon: Shield, title: 'Built by someone who lived it.', body: 'DojoDash wasn\u2019t built by a tech company that saw a gap in a spreadsheet. It was built by a World Champion athlete who spent years grinding to find clients despite having the credentials to back it up. That context is in every decision we made.' },
            ].map(({ icon: Icon, title, body }, i) => (
              <li key={i} className="flex gap-5">
                <div className="shrink-0 w-10 h-10 rounded-lg bg-[#C0A062]/10 border border-[#C0A062]/30 flex items-center justify-center">
                  <Icon className="w-5 h-5 text-[#C0A062]" />
                </div>
                <div>
                  <p className="text-white font-semibold leading-snug">{title}</p>
                  <p className="text-zinc-400 mt-1.5 leading-relaxed">{body}</p>
                </div>
              </li>
            ))}
          </ul>
        </div>
      </section>

      {/* CTA */}
      <section className="px-4 sm:px-6 lg:px-8 py-24">
        <div className="max-w-3xl mx-auto text-center">
          <h2 className="text-3xl sm:text-4xl font-bold text-white mb-6 leading-tight">
            Ready to train smarter, or coach more?
          </h2>
          <p className="text-zinc-400 mb-10 max-w-xl mx-auto">
            DojoDash is free to join. No subscriptions, no monthly fees. You only pay (or get paid) when a
            session actually happens.
          </p>
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <Button
              size="lg"
              onClick={() => navigate('/trainers')}
              className="bg-[#C0A062] text-black hover:bg-[#a98a52] font-semibold px-8"
              data-testid="about-cta-find-trainer"
            >
              Find a Trainer
              <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
            <Button
              size="lg"
              variant="outline"
              onClick={() => navigate(isAuthenticated && isTrainer ? '/dashboard' : '/become-a-trainer')}
              className="border-zinc-700 text-white hover:bg-zinc-800 px-8"
              data-testid="about-cta-become-trainer"
            >
              Become a Trainer
            </Button>
          </div>

          <p className="text-xs text-zinc-600 mt-12">
            By using DojoDash you agree to our{' '}
            <Link to="/terms" className="text-zinc-400 hover:text-white underline">Terms</Link> and{' '}
            <Link to="/privacy" className="text-zinc-400 hover:text-white underline">Privacy Policy</Link>.
          </p>
        </div>
      </section>
    </div>
  );
};

export default About;
